import { FC, ReactNode, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AlertColor, CircularProgress } from "@mui/material";
import PhFillCheckCircleSvg from "../../../assets/svg-fc/PhFillCheckCircleSvg";
import PhFillInfoSvg from "../../../assets/svg-fc/PhFillInfoSvg";
import PhFillWarningCircleSvg from "../../../assets/svg-fc/PhFillWarningCircleSvg";
import PhXSvg from "../../../assets/svg-fc/PhXSvg";
import "./toaster.scss";

interface iToasterProps {
	isOpen: boolean;
	message: string | undefined;
	title?: string;
	onClose: () => void;
	variant: AlertColor | undefined;
	className?: string;
	autoHideDuration?: number;
	icon?: ReactNode;
	action?: { label: string; onClick: () => void; disabled?: boolean };
	loading?: boolean;
	toastKey?: number;
	inline?: boolean;
	paused?: boolean;
	children?: ReactNode;
}

const AUTO_DISMISS_MS = 4200;

const DEFAULT_TITLE: Record<AlertColor, string> = {
	success: "Success",
	info: "Update",
	warning: "Action needed",
	error: "Action needed",
};

const ICONS: Record<AlertColor, ReactNode> = {
	success: <PhFillCheckCircleSvg size={17} />,
	info: <PhFillInfoSvg size={17} />,
	warning: <PhFillWarningCircleSvg size={17} />,
	error: <PhFillWarningCircleSvg size={17} />,
};

const Toaster: FC<iToasterProps> = ({
	isOpen,
	message,
	title,
	onClose,
	variant,
	className,
	autoHideDuration = AUTO_DISMISS_MS,
	icon,
	action,
	loading = false,
	toastKey,
	inline = false,
	paused = false,
	children,
}) => {
	const type: AlertColor = variant ?? "success";
	const onCloseRef = useRef(onClose);

	useEffect(() => {
		onCloseRef.current = onClose;
	});

	useEffect(() => {
		if (!isOpen || loading || paused) return undefined;
		const timer = setTimeout(() => onCloseRef.current(), autoHideDuration);
		return () => clearTimeout(timer);
	}, [isOpen, message, title, toastKey, loading, paused, autoHideDuration]);

	if (!isOpen || (!message && !title && !children)) return null;

	const toast = (
		<div
			key={toastKey}
			className={`ycToaster ycToaster--${type} ${loading ? "ycToaster--loading" : ""} ${className ?? ""}`}
			role={type === "error" || type === "warning" ? "alert" : "status"}
			aria-live="polite"
		>
			{children ?? (
				<>
					<span className="ycToaster__icon">
						{loading ? <CircularProgress size={15} thickness={5} color="inherit" /> : (icon ?? ICONS[type])}
					</span>
					<span className="ycToaster__text">
						<span className="ycToaster__title">{title || DEFAULT_TITLE[type]}</span>
						{message && <span className="ycToaster__message">{message}</span>}
					</span>
				</>
			)}
			{action && (
				<button
					type="button"
					className="ycToaster__action"
					disabled={loading || action.disabled}
					onClick={() => {
						onClose();
						action.onClick();
					}}
				>
					{action.label}
				</button>
			)}
			<button type="button" className="ycToaster__close" aria-label="Close" onClick={onClose}>
				<PhXSvg size={12} />
			</button>
		</div>
	);

	return inline ? toast : createPortal(toast, document.body);
};

export default Toaster;
